import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries) => {
      this.#visible = Boolean(entries[0]?.isIntersecting);
      if (this.#visible) this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  #visible = false;
  #refreshTimer = null;
  #isInteracting() {
    return this.contains(document.activeElement) || Boolean(
      this.querySelector('product-card:hover, quick-add-component[data-loading], quick-add-component[stay-visible]')
    );
  }
  #refresh = () => {
    const bounds = (this.closest('.shopify-section') || this).getBoundingClientRect();
    const nearby = bounds.bottom >= 0 && bounds.top <= window.innerHeight + 400;
    if (document.hidden || !nearby || this.#isInteracting()) return;
    this.#loadRecommendations(true);
  };

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#enhanceHeadings();
    this.#intersectionObserver.observe(this.closest('.shopify-section') || this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#refreshTimer = window.setInterval(this.#refresh, 30000);
    document.addEventListener('visibilitychange', this.#refresh);
  }

  #enhanceHeadings() {
    for (const heading of this.querySelectorAll('.section-resource-list__content h4')) {
      heading.setAttribute('role', 'heading');
      heading.setAttribute('aria-level', '2');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    window.clearInterval(this.#refreshTimer);
    document.removeEventListener('visibilitychange', this.#refresh);
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations(force = false) {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true' && !force) {
      return;
    }

    this.#fetchRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          if (force && this.#isInteracting()) return;
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          this.removeAttribute('data-error');
          await morphSection(sectionId, result.data, 'hydration', { injectStylesheet: true });
          this.#enhanceHeadings();
          for (const carousel of this.querySelectorAll('slideshow-component')) {
            carousel.refreshSlides();
          }
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches current recommendation markup without retaining inventory snapshots
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}&_bape_stock=${Math.floor(Date.now() / 30000)}`;

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
